import { IntuitSsoCallbackPayloadState, QuickbooksDataLinkingCallbackPayloadState } from '@common/neo.tax';

import { Path, getPathUrl, env } from '@neo.tax/shared/utils';

const AUTHORIZE_ENDPOINT = 'https://appcenter.intuit.com/connect/oauth2';

const IntuitScope = {
  Accounting: 'com.intuit.quickbooks.accounting',
  Address: 'address',
  Email: 'email',
  OpenId: 'openid',
  Phone: 'phone',
  Profile: 'profile',
} as const;

// encodeURIComponent leaves the RFC 3986 sub-delimiters `!'()*` unescaped, which are legal in a
// query string but ambiguous to a lenient parser. Escaping them keeps every value opaque.
const encodeComponent = (value: string): string =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
  );

const buildAuthorizeUrl = ({ scope, state }: { scope: readonly string[]; state: string }): string => {
  const parameters: Record<string, string> = {
    client_id: env.REACT_APP_QUICKBOOKS_CLIENT_ID,
    redirect_uri: `${env.REACT_APP_BACKEND_BASE_URL}/third-party-tokens/qb/callback`,
    response_type: 'code',
    scope: scope.join(' '),
    state,
  };

  const query = Object.entries(parameters)
    .map(([name, value]) => `${encodeComponent(name)}=${encodeComponent(value)}`)
    .join('&');

  return `${AUTHORIZE_ENDPOINT}?${query}`;
};

export const redirectToIntuitOAuth = (): void => {
  window.location.href = getIntuitSsoAuthUrl({
    isSso: true,
    successRedirectUrl: getPathUrl({ path: Path.LOGIN }),
    errorRedirectUrl: getPathUrl({ path: Path.LOGIN }),
  });
};

export const getIntuitSsoAuthUrl = (intuitSsoCallbackPayloadState: IntuitSsoCallbackPayloadState): string =>
  buildAuthorizeUrl({
    scope: [
      IntuitScope.Accounting,
      IntuitScope.OpenId,
      IntuitScope.Email,
      IntuitScope.Profile,
      IntuitScope.Phone,
      IntuitScope.Address,
    ],
    state: JSON.stringify(intuitSsoCallbackPayloadState),
  });

export const getQboDataLinkingAuthUrl = (
  quickbooksDataLinkingCallbackPayloadState: QuickbooksDataLinkingCallbackPayloadState,
): string =>
  buildAuthorizeUrl({
    scope: [IntuitScope.Accounting],
    state: JSON.stringify(quickbooksDataLinkingCallbackPayloadState),
  });
